import React, { useState, useEffect, useRef } from "react";
import {
	Container,
	ShowDisney,
	useGenericContext,
} from "@wdpr/groot-components";
import Cart from "./section/cart/Cart";
import LanguageSelector from "./LanguageSelector";
import SearchBar from "./SearchBar";
import appConfig from "config.json";
import { removeHoverOfSection } from "utils";
import Profile from "../profile/Profile";
import { Fragment } from "react";
import { AccessibleText } from "@wdpr/groot-components";
import Button from "components/shared/Button";
import Logo from "../Logo";
import ChromeLinks from "./ChromeLinks";
import StickyCta from "./StickyCta";
import BackButton from "./BackButton";
import { trackLink } from "utils/analyticsFramework";
import Section from "./section/Section";
import { getSvgBackground } from "utils/svg";
const navigationAppMenuLinksId = "navigationAppMenuLinks";
const { brands, focusableElements } = appConfig;
const syndicatedNavigationClass = "syndicated-navigation";
const syndicatedNavigationNavClass = `${syndicatedNavigationClass}__nav`;
const syndicatedNavigationToggleClass = `${syndicatedNavigationNavClass}__toggle`;
const syndicatedNavigationChromeClass = `${syndicatedNavigationClass}__chrome`;
const syndicatedNavigationClassHide = `${syndicatedNavigationClass}__hide`;
const syndicatedNavigationClassShow = `${syndicatedNavigationClass}__show`;
const largeAmountOfSections = 8;

const scrollTopAndLeft = (top = 0, left = 0) => {
	window?.scrollTo({
		top,
		left,
		behavior: "smooth",
	});
};

const Navigation = ({ showLogo }) => {
	const {
		config: {
			brand,
			chrome: showChrome,
			chromeLinks,
			languageSelector,
			locale,
			navigation: showNavigation,
			searchBar,
			showSections,
			store,
			showBackButton,
		},
		isMobileView,
		headerData: { navigation, chrome, backButton },
		profileData: { openedOneIdModal, userType },
		modal,
		cdnAssetsUrl,
	} = useGenericContext();

	const brandConfig = brands[brand] || {};
	const navRef = useRef(null);
	const sectionNavRef = useRef(null);
	const sectionRefs = useRef({});
	const [isMenuActive, setIsMenuActive] = useState(false);
	const [menuToggle, setMenuToggle] = useState(false);
	const [searchBarToggle, setSearchBarToggle] = useState(false);
	const [collapsedSection, setCollapsedSection] = useState("");
	const [logoLoaded, setLogoLoaded] = useState(false);
	const [showStickyCta, setShowStickyCta] = useState(false);
	const excludeAriaRole = brandConfig?.excludeAriaRole || false;
	const hideSearch = chrome?.hideSearch === "true";
	const showSearchBar =
		!store &&
		searchBar &&
		!brandConfig?.hideSearchBarForUserTypes?.includes(userType) &&
		chrome?.searchDisney &&
		!hideSearch;

	const hideMenuToggler = (!showSections && store) || !showNavigation;

	const navigationClasses = `${syndicatedNavigationNavClass} ${syndicatedNavigationNavClass}--${brand} ${syndicatedNavigationNavClass}--${brand}--${locale}`;

	useEffect(() => {
		document?.addEventListener("keydown", handleKeydown, true);
		if (isMobileView) {
			document?.addEventListener("click", handleClick, true);
			document?.addEventListener("focus", handleOutsideClick, true);
		}
		return () => {
			document?.removeEventListener("click", handleClick, true);
			document?.removeEventListener("focus", handleOutsideClick, true);
			document?.removeEventListener("keydown", handleKeydown, true);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (
			isMobileView &&
			collapsedSection &&
			sectionRefs.current[collapsedSection]
		) {
			sectionRefs.current[collapsedSection].scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
	}, [collapsedSection, isMobileView]);

	const onLogoLoadCallback = () => {
		setLogoLoaded(true);
	};

	const handleClick = (e) => {
		handleOutsideClick(e);
		handleClickOnSectionLinks(e);
	};

	const handleClickOnSectionLinks = (e) => {
		if (e.target.className) {
			const navigationLink = document?.querySelector(
				`.syndicated-section--open .syndicated-flyout .${e.target.className
					.split(" ")
					.filter((className) => !!className)
					.join(".")}`
			);
			navigationLink &&
				!validateNonClickableElement(e.target.className) &&
				handleCloseMenu();
		}
	};

	const validateNonClickableElement = (element) => {
		return (
			element.includes("syndicated-flyout__secondary") ||
			element.includes("syndicated-link-groups__list__item") ||
			element.includes("syndicated-title__subtitle") ||
			(element.includes("syndicated-title__heading") &&
				!element.includes("syndicated-title__heading--no-wrap"))
		);
	};

	const handleOutsideClick = (e) => {
		!sectionNavRef.current?.contains(e.target) &&
			!e.target?.classList?.contains(
				"syndicated-navigation__nav__toggle--menu"
			) &&
			handleCloseMenu();
	};

	const handleCloseMenu = () => {
		document?.body.classList?.remove("navigation-nav-open");
		setMenuToggle(false);
	};

	const handleMenuToggle = () => {
		document?.body.classList?.toggle("navigation-nav-open");
		scrollTopAndLeft();
		setMenuToggle((prevState) => !prevState);
		if (brands[brand]?.menuTracking) {
			trackLink({
				linkId: `${brand}-hamburger-${menuToggle ? "close" : "open"}`,
			});
		}
	};

	const handleSearchBarToggle = () => {
		isMobileView && !searchBarToggle && scrollTopAndLeft();
		setSearchBarToggle((prevState) => !prevState);
	};

	const handleSkipNavigation = () => {
		if (window) window.document.location.hash = "pageContentStart";
	};

	const handleKeydown = (e) => {
		if (e.key === "Escape") {
			removeHoverOfSection();
		}
	};

	const filterSections = () => {
		if (showNavigation || showLogo) {
			return isMobileView || store
				? navigation
				: navigation?.filter(
						(section) => !brandConfig.sectionsInChromeBar?.includes(section.id)
				  );
		} else {
			return [];
		}
	};

	const renderSections = (filteredSections = []) => {
		let isLastSection = false;

		return (
			showSections &&
			filteredSections?.map((section, key) => {
				isLastSection = isMobileView
					? false
					: key === filteredSections?.length - 1;
				return section.id === "cart" ? (
					<Cart
						key={key}
						{...section}
						isMenuActive={isMenuActive}
						setIsMenuActive={setIsMenuActive}
						isMobileView={isMobileView}
						isLastSection={isLastSection}
					/>
				) : (
					<Section
						{...section}
						setCollapsedSection={setCollapsedSection}
						collapse={
							!isMobileView || (isMobileView && collapsedSection === section.id)
						}
						key={key}
						ref={(el) => (sectionRefs.current[section.id] = el)}
						isMenuActive={isMenuActive}
						setIsMenuActive={setIsMenuActive}
						store={store}
						brand={brand}
						isLastSection={isLastSection}
						handleFullClose={() => {
							handleCloseMenu();
							removeHoverOfSection();
						}}
					/>
				);
			})
		);
	};

	useEffect(() => {
		if (!isMobileView) {
			setSearchBarToggle(false);
		}
		if (brandConfig.classToIndicateMobileView) {
			document?.body.classList[isMobileView ? "add" : "remove"](
				brandConfig.classToIndicateMobileView
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMobileView]);

	useEffect(() => {
		if ((modal || openedOneIdModal) && menuToggle) {
			handleMenuToggle();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modal, openedOneIdModal, menuToggle]);

	useEffect(() => {
		if (isMobileView && menuToggle) {
			const focusable =
				sectionNavRef?.current?.querySelector(focusableElements);
			setTimeout(() => {
				focusable?.focus();
			}, 200);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [menuToggle]);

	const filteredSections = filterSections();

	const renderNavigationContent = () => {
		return showBackButton ? (
			<div
				className={`${navigationClasses} ${syndicatedNavigationNavClass}--with-back-button`}
			>
				{isMobileView ? (
					<Fragment>
						<BackButton isMobileView />
						<Logo onLoadCallback={onLogoLoadCallback} />
						{backButton && (
							<div style={{ width: "20px", margin: "0 17px" }}></div>
						)}
					</Fragment>
				) : (
					<Fragment>
						<Logo onLoadCallback={onLogoLoadCallback} />
						<BackButton />
					</Fragment>
				)}
			</div>
		) : (
			<Fragment>
				{showChrome && (
					<div className={syndicatedNavigationChromeClass}>
						<ShowDisney />
						<Button
							className={`${syndicatedNavigationChromeClass}__skip-navigation`}
							customStyles
							onClick={handleSkipNavigation}
							tabIndex={1}
						>
							{chrome?.skipNavigation}
						</Button>
						<div
							className={`${syndicatedNavigationChromeClass}__links ${syndicatedNavigationChromeClass}__links--${brand}`}
						>
							{!isMobileView && (
								<Profile username reservation userStatusLinks newsletter />
							)}
							{languageSelector && <LanguageSelector />}
							{chromeLinks && (
								<ChromeLinks brand={brand} elements={chrome?.elements} cdnAssetsUrl={cdnAssetsUrl} />
							)}
						</div>
						{!isMobileView && showSearchBar && <SearchBar />}
					</div>
				)}
				{(showNavigation || showLogo) && (
					<div className={navigationClasses}>
						{!isMobileView && <Logo onLoadCallback={onLogoLoadCallback} />}
						{showNavigation && (
							<nav
								role="navigation"
								id={navigationAppMenuLinksId}
								aria-label={chrome?.accessibiltyTextNavigationLinks}
							>
								<div
									ref={sectionNavRef}
									className={`${syndicatedNavigationNavClass}__sections ${
										menuToggle
											? `${syndicatedNavigationNavClass}__sections--open`
											: ""
									}`}
									role={
										excludeAriaRole || isMobileView || isMenuActive
											? null
											: "menubar"
									}
								>
									{!isMobileView && (
										<AccessibleText>
											{chrome?.accessibiltyTextNavigationLinks}
										</AccessibleText>
									)}
									{isMobileView && <Profile isMobileView username />}
									{renderSections(filteredSections)}
									{isMobileView && !store && (
										<Fragment>
											{languageSelector && <LanguageSelector />}
											<Profile reservation userStatusLinks showIcon />
											<ShowDisney />
											{chromeLinks &&
												brandConfig?.chromeLinks?.showOnMobile && (
													<ChromeLinks
														brand={brand}
														elements={chrome?.elements}
														mobileIcon={brandConfig.chromeLinks.mobileIcon}
														cdnAssetsUrl={cdnAssetsUrl}
													/>
												)}
										</Fragment>
									)}
								</div>
							</nav>
						)}
						{isMobileView && (
							<Fragment>
								<Button
									ariaControls={navigationAppMenuLinksId}
									elementId="mobile-nav-toggle"
									ariaExpanded={menuToggle}
									accessibleText={chrome?.accessibiltyTextShowNavigation}
									className={`${syndicatedNavigationToggleClass} ${syndicatedNavigationToggleClass}--${brand} ${syndicatedNavigationToggleClass}--menu ${
										hideMenuToggler
											? `${syndicatedNavigationToggleClass}--menu--hidden`
											: ""
									}`}
									customStyles
									onClick={handleMenuToggle}
									style={getSvgBackground()}
									disabled={hideMenuToggler}
									tabIndex={2}
								/>
								<Logo
									moveLeft={showStickyCta}
									onLoadCallback={onLogoLoadCallback}
								/>
								{!store && (
									<StickyCta
										setShowStickyCta={setShowStickyCta}
										showStickyCta={showStickyCta}
									/>
								)}
								<Button
									ariaExpanded={searchBarToggle}
									accessibleText={chrome?.accessibiltyTextShowSearch}
									className={`${syndicatedNavigationToggleClass} ${syndicatedNavigationToggleClass}--${brand} ${syndicatedNavigationToggleClass}--search ${
										showSearchBar
											? ""
											: `${syndicatedNavigationToggleClass}--search--hidden`
									}`}
									customStyles
									onClick={handleSearchBarToggle}
									style={getSvgBackground("search")}
									disabled={!showSearchBar}
								/>
							</Fragment>
						)}
					</div>
				)}
				{isMobileView && searchBarToggle && showNavigation && showSearchBar && (
					<SearchBar />
				)}
			</Fragment>
		);
	};

	return (
		<div
			className={
				syndicatedNavigationClass +
				(store ? ` ${syndicatedNavigationClass}--store tradeHeader` : "") +
				` ${
					logoLoaded
						? syndicatedNavigationClassShow
						: syndicatedNavigationClassHide
				}`
			}
			ref={navRef}
		>
			<Container large={filteredSections?.length >= largeAmountOfSections}>
				{renderNavigationContent()}
			</Container>
		</div>
	);
};

export default Navigation;
